/**
 * Breach Workflow Form Validation
 * Validates each step and returns errors with field references
 */

export const validateStep1 = (formData) => {
  const errors = {};

  if (!formData.entity_id) {
    errors.entity_id = 'Entity is required';
  }

  // is_personal_data_breach can be: true (Yes), false (No), null (Unknown)
  // undefined means not yet selected - only that is an error
  if (formData.is_personal_data_breach === undefined) {
    errors.is_personal_data_breach = 'Please classify this incident';
  }

  // Additional validation only if it's marked as a breach (true)
  // If false (No) or null (Unknown), no additional fields required
  if (formData.is_personal_data_breach === true) {
    if (!formData.breach_reference) {
      errors.breach_reference = 'Breach reference is required';
    }
    if (!formData.title) {
      errors.title = 'Breach title is required';
    }
    if (!formData.awareness_date) {
      errors.awareness_date = 'Awareness date is required (sets 72-hour deadline)';
    }
    if (!formData.what_happened) {
      errors.what_happened = 'Description of incident is required';
    }
  }

  return errors;
};

export const validateStep2 = (formData) => {
  const errors = {};

  // Systems affected can be an array of tags or empty
  // Only validate if it exists and is an array
  if (Array.isArray(formData.systems_affected)) {
    if (formData.systems_affected.length === 0) {
      // Array is empty - this could be optional or required depending on workflow
      // For now, make it optional to allow progression
    }
  }

  // No strict validation for Step 2 for now
  // TODO: Define which fields are actually required in Step 2

  return errors;
};

export const validateStep3 = (formData) => {
  const errors = {};

  // No strict validation for Step 3 for now
  // TODO: Define which fields are actually required in Step 3

  return errors;
};

export const validateStep4 = () => {
  // No strict validation for Step 4 for now
  // TODO: Define which fields are actually required in Step 4
  return {};
};

export const validateStep5 = (formData) => {
  const errors = {};

  // What actions have been taken? - Required field
  if (!formData.actions_taken || (typeof formData.actions_taken === 'string' && formData.actions_taken.trim() === '')) {
    errors.actions_taken = 'Describe the actions taken in response to the incident';
  }

  return errors;
};

export const validateStep6 = (formData) => {
  const errors = {};

  // If DPO override is active, justification is required
  if (formData.override_severity) {
    if (!formData.override_justification || (typeof formData.override_justification === 'string' && formData.override_justification.trim() === '')) {
      errors.override_justification = 'DPO override justification is required';
    }
  }

  return errors;
};

export const validateStep7 = (formData) => {
  const errors = {};

  if (formData.dpo_reporting_decision) {
    if (formData.dpo_reporting_decision === 'report') {
      if (!formData.confirmed_deadline) {
        errors.confirmed_deadline = 'Confirmed deadline is required for notification';
      }
    }

    // notification_reason is mandatory when DPO chooses Internal Record Only
    if (formData.dpo_reporting_decision === 'no_report') {
      if (!formData.notification_reason || !formData.notification_reason.trim()) {
        errors.dpo_justification = 'Reason is required when selecting Internal Record Only';
      }
    }
  }

  return errors;
};

export const validateStep8 = (formData) => {
  const errors = {};

  // If remediation actions are added, each must have required fields
  const actions = formData.remediation_actions || [];
  if (actions.length > 0) {
    actions.forEach((action, index) => {
      if (!action.action || (typeof action.action === 'string' && action.action.trim() === '')) {
        errors[`remediation_action_${index}_action`] = `Action ${index + 1}: Remediation action description is required`;
      }
      if (!action.owner || (typeof action.owner === 'string' && action.owner.trim() === '')) {
        errors[`remediation_action_${index}_owner`] = `Action ${index + 1}: Owner is required`;
      }
      if (!action.deadline) {
        errors[`remediation_action_${index}_deadline`] = `Action ${index + 1}: Deadline is required`;
      }
    });
  }

  return errors;
};

export const validateStep9 = (formData) => {
  const errors = {};

  // DPO Reporting Decision - Can be 'pending' for draft, but not empty
  if (!formData.dpo_reporting_decision) {
    errors.dpo_reporting_decision = 'DPO decision is required';
  }
  // Note: 'pending' is allowed during form navigation
  // Final submission should check for explicit decision, but that's a backend concern

  return errors;
};

/**
 * Master validation function that routes to step-specific validators
 */
export const validateStep = (step, formData) => {
  const validators = {
    1: validateStep1,
    2: validateStep2,
    3: validateStep3,
    4: validateStep4,
    5: validateStep5,
    6: validateStep6,
    7: validateStep7,
    8: validateStep8,
    9: validateStep9,
  };

  const validator = validators[step];
  if (!validator) {
    return {};
  }

  return validator(formData);
};

/**
 * Find first invalid field and return its ref
 */
export const getFirstErrorFieldRef = (errors) => {
  const fieldNames = Object.keys(errors);
  if (fieldNames.length === 0) return null;
  return fieldNames[0];
};
